import { useState } from 'react';
import { FieldConfig } from '../types/template';
import LogoPicker from './LogoPicker';

interface FieldEditorProps {
  field: FieldConfig;
  fieldIndex: number;
  onChange: (field: FieldConfig) => void;
  onClose: () => void;
  demoMode?: boolean;
}

export default function FieldEditor({ field, fieldIndex, onChange, onClose, demoMode }: FieldEditorProps) {
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>Edit Field: {field.label}</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ×
          </button>
        </div>

        {/* Grid Layout - Available for all fields except column breaks */}
        {field.variable !== 'columnBreak' && (
          <>
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
              <strong>📐 Grid Positioning (Optional)</strong>
              <p style={{ fontSize: '12px', color: '#666', margin: '8px 0' }}>
                Use these settings to position fields in a table-like grid. Leave blank for normal sequential layout.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600' }}>Row</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={field.gridRow ?? fieldIndex}
                    onChange={(e) => onChange({ ...field, gridRow: e.target.value ? parseInt(e.target.value) : undefined })}
                    style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <small style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Default: Row {fieldIndex}</small>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600' }}>Column</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    placeholder="1"
                    value={field.gridCol ?? ''}
                    onChange={(e) => onChange({ ...field, gridCol: e.target.value ? parseInt(e.target.value) : undefined })}
                    style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <small style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Default: Col 1</small>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600' }}>Width (px)</label>
                  <input
                    type="number"
                    min="0"
                    max="500"
                    placeholder="150"
                    value={field.gridWidth ?? ''}
                    onChange={(e) => onChange({ ...field, gridWidth: e.target.value ? parseInt(e.target.value) : undefined })}
                    style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <small style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Default: 150px</small>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600' }}>Height (px)</label>
                  <input
                    type="number"
                    min="0"
                    max="200"
                    placeholder="auto"
                    value={field.gridHeight ?? ''}
                    onChange={(e) => onChange({ ...field, gridHeight: e.target.value ? parseInt(e.target.value) : undefined })}
                    style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <small style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Default: auto</small>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600' }}>Height (rows)</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    placeholder="1"
                    value={field.gridRowspan ?? ''}
                    onChange={(e) => onChange({ ...field, gridRowspan: e.target.value ? parseInt(e.target.value) : undefined })}
                    style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <small style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>For spanning rows</small>
                </div>
              </div>
              
              <small style={{ display: 'block', marginTop: '8px', fontSize: '11px', color: '#666' }}>
                <strong>Row:</strong> Horizontal line (1=first, 2=second, etc.)<br/>
                <strong>Column:</strong> Vertical column (1=first, 2=second, etc.)<br/>
                <strong>Width (px):</strong> Fixed width for this cell (default: 150px)<br/>
                <strong>Height (px):</strong> Fixed height for this cell (default: auto)<br/>
                <strong>Height (rows):</strong> Span multiple rows (for logos/images)
              </small>
            </div>
          </>
        )}

        {/* Column Break - Special handling */}
        {field.variable === 'columnBreak' ? (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Column Left Position (px)
            </label>
            <input
              type="number"
              min="0"
              max="500"
              value={field.columnSpacing ?? 250}
              onChange={(e) => {
                const val = e.target.value;
                onChange({ ...field, columnSpacing: val === '' ? 250 : parseInt(val) });
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Position from the left edge where this column starts. Set to 0 to start a new row below.
            </small>
            <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
              <strong>💡 Tip:</strong> Control both horizontal and vertical positioning:
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                <li><strong>0px:</strong> Start a new row below all previous content</li>
                <li>150-200px: New column close to left edge</li>
                <li>250-300px: New column at standard position (recommended)</li>
                <li>350-400px: New column far from left edge</li>
              </ul>
            </div>
          </div>
        ) : (field.variable === 'logo' || field.variable === 'secondaryLogo') ? (
          // Logo field - Special handling
          <>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {field.variable === 'logo' ? 'Primary Logo' : 'Secondary Logo'}
              </label>
              {demoMode ? (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#1e40af',
                  textAlign: 'center',
                }}>
                  🔒 Logo editing disabled in demo mode
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowLogoPicker(true)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#0066cc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: '100%',
                  }}
                >
                  {field.logoUrl ? '✓ Change Logo' : '📁 Browse Logo Library'}
                </button>
              )}
              {field.logoUrl && (
                <div style={{ marginTop: '15px', textAlign: 'center' }}>
                  <img 
                    src={field.logoUrl} 
                    alt="Selected Logo" 
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '100px', 
                      display: 'block', 
                      margin: '0 auto',
                      border: '1px solid #ddd', 
                      borderRadius: '4px', 
                      padding: '10px',
                      backgroundColor: '#f9f9f9'
                    }} 
                  />
                  <button
                    type="button"
                    onClick={() => onChange({ ...field, logoUrl: undefined })}
                    style={{
                      marginTop: '10px',
                      padding: '6px 12px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Remove Logo
                  </button>
                </div>
              )}
            </div>
            
            {/* Logo Width */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Logo Width (px)
              </label>
              <input
                type="number"
                min="20"
                max="400"
                value={field.logoWidth || 100}
                onChange={(e) => onChange({ ...field, logoWidth: parseInt(e.target.value) || 100 })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Width of the logo in the signature. Height will scale proportionally.
              </small>
            </div>
          </>
        ) : (
          // Regular field editing options
          <>
        {/* Custom Text Content - Only for custom text fields */}
        {(field.variable === 'customText' || field.id.startsWith('text_')) && (
          <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              ✏️ Custom Text Content
            </label>
            <textarea
              placeholder="Enter your custom text here..."
              value={field.customText || ''}
              onChange={(e) => onChange({ ...field, customText: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minHeight: '60px',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              This text will appear in your signature. You can use line breaks and formatting.
            </small>
          </div>
        )}

        {/* Website URL - Only for website field */}
        {(field.id === 'website' || field.variable === 'website') && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              🌐 Website URL
            </label>
            <input
              type="url"
              placeholder="https://example.com"
              value={field.websiteUrl || ''}
              onChange={(e) => onChange({ ...field, websiteUrl: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              The website URL to link to. If empty, will use user's website field from profile.
            </small>
          </div>
        )}

        {/* Prefix/Label */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Prefix Label
          </label>
          <input
            type="text"
            placeholder={`e.g., "E:", "Email:", "Phone:"`}
            value={field.prefix || ''}
            onChange={(e) => onChange({ ...field, prefix: e.target.value })}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
          <small style={{ color: '#666', fontSize: '12px' }}>
            Text shown before the field value (leave empty for none)
          </small>
        </div>

        {/* Prefix Color */}
        {field.prefix && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Prefix Color
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="color"
                value={field.prefixColor || '#000000'}
                onChange={(e) => onChange({ ...field, prefixColor: e.target.value })}
                style={{ width: '50px', height: '40px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                value={field.prefixColor || '#000000'}
                onChange={(e) => onChange({ ...field, prefixColor: e.target.value })}
                placeholder="#000000"
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                }}
              />
              {field.prefixColor && field.prefixColor !== '#000000' && (
                <button
                  type="button"
                  onClick={() => onChange({ ...field, prefixColor: undefined })}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Reset
                </button>
              )}
            </div>
            <small style={{ color: '#666', fontSize: '12px' }}>
              Color for the prefix only (separate from field value color)
            </small>
          </div>
        )}

        {/* Bold */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={field.bold || false}
              onChange={(e) => onChange({ ...field, bold: e.target.checked })}
              style={{ marginRight: '8px' }}
            />
            <strong>Bold text</strong>
          </label>
        </div>

        {/* Italic */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={field.italic || false}
              onChange={(e) => onChange({ ...field, italic: e.target.checked })}
              style={{ marginRight: '8px' }}
            />
            <em>Italic text</em>
          </label>
        </div>

        {/* Underline */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={field.underline || false}
              onChange={(e) => onChange({ ...field, underline: e.target.checked })}
              style={{ marginRight: '8px' }}
            />
            <span style={{ textDecoration: 'underline' }}>Underline text</span>
          </label>
        </div>

        {/* Font Family */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Font Family
          </label>
          <select
            value={field.fontFamily || ''}
            onChange={(e) => onChange({ ...field, fontFamily: e.target.value || undefined })}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            <option value="">Use Global Font</option>
            <option value="Arial, sans-serif">Arial</option>
            <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
            <option value="'Segoe UI', Tahoma, sans-serif">Segoe UI</option>
            <option value="'Times New Roman', Times, serif">Times New Roman</option>
            <option value="Georgia, serif">Georgia</option>
            <option value="'Courier New', Courier, monospace">Courier New</option>
            <option value="Verdana, sans-serif">Verdana</option>
          </select>
          <small style={{ color: '#666', fontSize: '12px' }}>
            Leave as "Use Global Font" to inherit from template settings
          </small>
        </div>

        {/* Font Size */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Font Size
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="number"
              min="8"
              max="72"
              placeholder="Global"
              value={field.fontSize || ''}
              onChange={(e) => onChange({ ...field, fontSize: e.target.value || undefined })}
              style={{
                flex: 1,
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
            <span style={{ color: '#666' }}>px</span>
            {field.fontSize && (
              <button
                type="button"
                onClick={() => onChange({ ...field, fontSize: undefined })}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Reset
              </button>
            )}
          </div>
          <small style={{ color: '#666', fontSize: '12px' }}>
            Leave empty to use global font size from template
          </small>
        </div>

        {/* Color */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Text Color
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="color"
              value={field.color || '#000000'}
              onChange={(e) => onChange({ ...field, color: e.target.value })}
              style={{ width: '50px', height: '40px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <input
              type="text"
              value={field.color || '#000000'}
              onChange={(e) => onChange({ ...field, color: e.target.value })}
              placeholder="#000000"
              style={{
                flex: 1,
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'monospace',
              }}
            />
            {field.color && field.color !== '#000000' && (
              <button
                type="button"
                onClick={() => onChange({ ...field, color: undefined })}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Reset
              </button>
            )}
          </div>
          <small style={{ color: '#666', fontSize: '12px' }}>
            Leave as black (#000000) to use default color
          </small>
        </div>


        </>
        )}

        {/* Preview */}
        <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '12px', color: '#666' }}>
            PREVIEW
          </label>
          <div style={{ fontSize: '14px' }}>
            {(field.variable === 'customText' || field.id.startsWith('text_')) ? (
              // Custom text field preview
              <>
                {field.prefix && (
                  <span style={{ color: field.prefixColor || '#000000' }}>
                    {field.prefix}{' '}
                  </span>
                )}
                <span
                  style={{
                    fontWeight: field.bold ? 'bold' : 'normal',
                    fontStyle: field.italic ? 'italic' : 'normal',
                    textDecoration: field.underline ? 'underline' : 'none',
                    color: field.color || '#000000',
                    fontFamily: field.fontFamily || 'inherit',
                    fontSize: field.fontSize ? `${field.fontSize}px` : 'inherit',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {field.customText || 'Enter your custom text above...'}
                </span>
              </>
            ) : (field.id === 'website' || field.variable === 'website') && field.websiteUrl ? (
              <a href={field.websiteUrl} style={{ color: field.color || '#0066cc', textDecoration: 'underline' }}>
                {field.websiteUrl.replace(/^https?:\/\//, '')}
              </a>
            ) : (
              <>
                {field.prefix && (
                  <span style={{ color: field.prefixColor || '#000000' }}>
                    {field.prefix}{' '}
                  </span>
                )}
                <span
                  style={{
                    fontWeight: field.bold ? 'bold' : 'normal',
                    fontStyle: field.italic ? 'italic' : 'normal',
                    textDecoration: field.underline ? 'underline' : 'none',
                    color: field.color || '#000000',
                    fontFamily: field.fontFamily || 'inherit',
                    fontSize: field.fontSize ? `${field.fontSize}px` : 'inherit',
                  }}
                >
                  {`{{${field.variable}}}`}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Done
          </button>
        </div>
      </div>
      
      {/* Logo Picker Modal */}
      {showLogoPicker && (
        <LogoPicker
          currentUrl={field.logoUrl || ''}
          onSelect={(url) => {
            onChange({ ...field, logoUrl: url });
            setShowLogoPicker(false);
          }}
          onClose={() => setShowLogoPicker(false)}
        />
      )}
    </div>
  );
}
